import styled from 'styled-components';
import { useQuantContext } from '../../../context/QuantContext';

interface Props {
  p1: string;
  p2: string;
  p3: string;
  p4: string;
}
function ResultTable({ p1, p2, p3, p4 }: Props) {
  const { selectedRegions, selectedCategory, noItem } = useQuantContext();
  const hasRegion = selectedRegions.length > 0;
  const categoryText =
    selectedCategory.length === 0
      ? '물건 전체'
      : selectedCategory.length > 1
        ? `${selectedCategory[0]} 외`
        : `${selectedCategory[0]}`;

  const regionText =
    selectedRegions.length > 1
      ? `${selectedRegions[0]} 외`
      : `${selectedRegions[0] ?? ''}`;

  const addonCount = Math.max(selectedCategory.length - 1, 0);
  const addonRegionCount = Math.max(selectedRegions.length - 1, 0);

  const noItemText = '테마에 해당하는 물건 없음';

  return (
    <Inner>
      <Table>
        <colgroup>
          <col width={96} />
          <col />
          <col />
        </colgroup>

        <tbody>
          <Tr>
            <ThLeft scope="row">물건</ThLeft>
            <TdCategory colSpan={2} $hasSelect={selectedCategory.length > 0}>
              <CellContent>
                {categoryText}
                {selectedCategory.length > 1 && <Addon>+{addonCount}</Addon>}
              </CellContent>
            </TdCategory>
          </Tr>
          <Tr>
            <ThLeft scope="row">지역</ThLeft>
            <Td colSpan={hasRegion ? 1 : 2}>전국</Td>
            {hasRegion && (
              <TdSelected $hasSelect>
                <CellContent>
                  {regionText}
                  {selectedRegions.length > 1 && (
                    <Addon>+{addonRegionCount}</Addon>
                  )}
                </CellContent>
              </TdSelected>
            )}
          </Tr>
          <Tr>
            <ThLeft scope="row">예상 수익률</ThLeft>
            <Td colSpan={hasRegion ? 1 : 2}>
              {noItem ? '테마에 해당하는 물건 없음' : `${p1}%`}
            </Td>
            {hasRegion && (
              <TdSelected $hasSelect>
                {noItem ? '테마에 해당하는 물건 없음' : `${p3}%`}
              </TdSelected>
            )}
          </Tr>
          <Tr>
            <ThLeft scope="row">최대 손실률</ThLeft>
            <Td colSpan={hasRegion ? 1 : 2}>
              {noItem ? '테마에 해당하는 물건 없음' : `${p2}%`}
            </Td>
            {hasRegion && (
              <TdSelected $hasSelect>
                {noItem ? '테마에 해당하는 물건 없음' : `${p4}%`}
              </TdSelected>
            )}
          </Tr>
        </tbody>
      </Table>

      <Description>
        <img src="/assets/images/icon/info-dark.svg" alt="" />
        <span>
          예상 수익률은 ‘매각 수익률’입니다. 임대 수익률은 추후 제공 예정입니다.
        </span>
      </Description>
    </Inner>
  );
}

export default ResultTable;

const Inner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
  font-size: 14px;
  line-height: 145%;
  border-radius: 16px;
  overflow: hidden;
`;

const Tr = styled.tr`
  /* 행 구분선 필요 시 여기에 border-bottom 추가 가능 */
`;

const ThLeft = styled.th`
  background: #f7f8fc;
  color: #616161;
  font-weight: 500;
  text-align: left;
  padding: 12px;
  white-space: nowrap;
`;

const CellContent = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
`;

const baseTd = `
  padding: 12px;
  color: #222;
  font-weight: 500;
  vertical-align: middle;
  text-align: center;
  word-break: keep-all;
`;

const Td = styled.td`
  ${baseTd}
  background: #fff;
`;

const TdCenter = styled.td<{ $hasSelect?: boolean; $roundedRight?: boolean }>`
  ${baseTd}
  background: ${({ $hasSelect }) => ($hasSelect ? '#008AFF0F' : '#fff')};

  ${({ $roundedRight }) =>
    $roundedRight &&
    `
    border-bottom-right-radius: 16px;
  `}
`;

const TdCategory = styled.td<{ $hasSelect?: boolean }>`
  ${baseTd}
  background: ${({ $hasSelect }) => ($hasSelect ? '#008AFF0F' : '#fff')};
  text-align: center;
  border-top-right-radius: 16px;
`;

const TdSelected = styled.td<{ $hasSelect?: boolean }>`
  ${baseTd}
  background: ${({ $hasSelect }) => ($hasSelect ? '#008AFF0F' : '#fff')};
  text-align: center;
`;

const Addon = styled.span`
  display: inline-flex;
  padding: 2px 12px;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  background: #008aff;
  font-size: 12px;
  font-weight: 700;
  line-height: 145%;
  letter-spacing: -0.24px;
  color: #fff;
`;

const Description = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;

  color: #6b7684;
  font-size: 13px;
  font-weight: 500;
  line-height: 150%; /* 19.5px */
  letter-spacing: -0.26px;
`;
